import { Box } from '@mui/material';
import { JSX, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { DueDiligenceControlConfiguration } from 'Models/DueDiligence';

import styles from './Modal.module.css';

export interface ControlModalProps {
    existingNames: string[];
    hideModal: () => void;
    onSave: (control: DueDiligenceControlConfiguration) => void;

    /** If provided, the modal is in edit mode with this control pre-populated. */
    control?: DueDiligenceControlConfiguration;
}

/**
 * Modal for adding or editing a control.
 * In add mode: Creates a new control with empty questions.
 * In edit mode: Updates the control's name and text.
 */
export const ControlModal = (props: ControlModalProps): JSX.Element => {
    const { control, existingNames, hideModal, onSave } = props;
    const isEditMode = !!control;

    const [name, setName] = useState(control?.name ?? '');
    const [text, setText] = useState(control?.text ?? '');
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setText(event.target.value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        setErrorMessage(undefined);

        const trimmedName = name.trim();
        const trimmedText = text.trim();

        if (trimmedName.length === 0) {
            setErrorMessage('Name is required.');
            return;
        }

        if (existingNames.includes(trimmedName)) {
            setErrorMessage('A control with this name already exists in this group.');
            return;
        }

        if (trimmedText.length === 0) {
            setErrorMessage('Control text is required.');
            return;
        }

        const updatedControl: DueDiligenceControlConfiguration = {
            name: trimmedName,
            questions: control?.questions ?? [],
            text: trimmedText,
        };

        onSave(updatedControl);
    };

    return (
        <Modal open header={isEditMode ? 'Edit Control' : 'Add Control'}>
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.formField}>
                    <FormFieldText formFieldId="name" formFieldLabel="Name" value={name} handleChange={handleNameChange} required />
                </div>

                <div className={styles.formField}>
                    <FormFieldTextArea formFieldId="text" formFieldLabel="Control Text" value={text} handleChange={handleTextChange} required tooltip="The control requirement or description." />
                </div>

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Cancel
                    </Button>
                    <Button variant="submit">Save</Button>
                </div>
            </Box>
        </Modal>
    );
};
